import { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStop } from "@fortawesome/free-solid-svg-icons";
import UploadPopup from "./Upload-Popup";
import { useLocation } from "react-router-dom";
import WaveAmplifier from "./Wavecomponent"; // Ensure this points to the correct file
import { IoMdArrowRoundBack } from "react-icons/io";
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { FaPlay, FaPause } from "react-icons/fa";

const VideoRecording = () => {
  const ffmpeg = useRef(new FFmpeg());
  const [ffmpegLoaded, setFFmpegLoaded] = useState(false);
  const location = useLocation();

  const [mode] = useState(() => {
    const queryParams = new URLSearchParams(location.search);
    const urlMode = queryParams.get('mode');
    if (urlMode) {
      sessionStorage.setItem('recordingMode', urlMode);
      return urlMode;
    }
    const storedMode = sessionStorage.getItem('recordingMode');
    return storedMode || location.state?.mode || 'video';
  });

  const [showComponent, setShowComponent] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoURL, setVideoURL] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState(null);
  const [currentDateTime, setCurrentDateTime] = useState('');
  const [userName, setUserName] = useState("");
  const [userInitial, setUserInitial] = useState("");
  const [isSafari, setIsSafari] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [showStartMessage, setShowStartMessage] = useState(false);
  const [hasStartedRecording, setHasStartedRecording] = useState(false);
  const dateTimeIntervalRef = useRef(null);
  const hasRecordedMedia = videoBlob || audioBlob;
  const [showTabSwitchWarning, setShowTabSwitchWarning] = useState(!hasRecordedMedia);

  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const stopTimerRef = useRef(null);
  const intervalRef = useRef(null);
  const actualRecordingDuration = useRef(0);
  const startTimeRef = useRef(0);
  const countdownIntervalRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem('recordingMode', mode);
  }, [mode]);

  const toggleFullScreen = () => {
    const videoContainer = videoRef.current?.parentElement;
    if (!document.fullscreenElement) {
      videoContainer?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isSafariBrowser = /safari/.test(userAgent) && !/chrome/.test(userAgent) && !/firefox/.test(userAgent);
    const isIOSDevice = /ipad|iphone|ipod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroidDevice = /android/.test(userAgent);

    setIsSafari(isSafariBrowser);
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);
  }, []);

  const processVideoWithFFmpeg = useCallback(async (blob) => {
    const inputName = 'input.mp4';
    const outputName = 'output.mp4';

    await ffmpeg.current.writeFile(inputName, await fetchFile(blob));

    const ffmpegArgs = [
      '-i', inputName,
      '-c:v', 'copy',
      '-c:a', 'aac',
      '-movflags', '+faststart',
      '-fflags', '+genpts',
      '-avoid_negative_ts', 'make_zero',
      '-map', '0:v:0?',
      '-map', '0:a:0?',
    ];

    if ((isSafari || isIOS || isAndroid) && actualRecordingDuration.current > 0) {
      ffmpegArgs.push('-t', actualRecordingDuration.current.toString());
    }

    ffmpegArgs.push(outputName);

    await ffmpeg.current.exec(ffmpegArgs);

    const data = await ffmpeg.current.readFile(outputName);
    return new Blob([data], { type: 'video/mp4' });
  }, [isSafari, isIOS, isAndroid]);

  const processAudioWithFFmpeg = useCallback(async (blob) => {
    const inputName = 'input.webm';
    const outputName = 'output.mp4';

    await ffmpeg.current.writeFile(inputName, await fetchFile(blob));

    const ffmpegArgs = [
      '-i', inputName,
      '-vn',
      '-c:a', 'aac',
      '-b:a', '128k',
      '-ar', '44100',
      '-ac', '2',
    ];

    if ((isSafari || isIOS || isAndroid) && actualRecordingDuration.current > 0) {
      ffmpegArgs.push('-t', actualRecordingDuration.current.toString());
    }

    ffmpegArgs.push(outputName);

    await ffmpeg.current.exec(ffmpegArgs);

    const data = await ffmpeg.current.readFile(outputName);
    return new Blob([data], { type: 'audio/mp4' });
  }, [isSafari, isIOS, isAndroid]);

  const fixIOSSafariDuration = useCallback(async (blob, duration) => {
    if (!isIOS && !isSafari) return blob;

    try {
      const tempVideo = document.createElement('video');
      const objectURL = URL.createObjectURL(blob);

      return new Promise((resolve) => {
        tempVideo.onloadedmetadata = async () => {
          if (Math.abs(tempVideo.duration - duration) > 1) {
            const reprocessedBlob = await processVideoWithFFmpeg(blob);
            URL.revokeObjectURL(objectURL);
            resolve(reprocessedBlob);
          } else {
            URL.revokeObjectURL(objectURL);
            resolve(blob);
          }
        };

        tempVideo.onerror = () => {
          URL.revokeObjectURL(objectURL);
          resolve(blob);
        };

        tempVideo.src = objectURL;
        tempVideo.load();
      });
    } catch (error) {
      return blob;
    }
  }, [isSafari, isIOS, processVideoWithFFmpeg]);

  useEffect(() => {
    const loadFFmpeg = async () => {
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      ffmpeg.current.on('log', ({ message }) => console.log(message));
      ffmpeg.current.on('progress', ({ progress }) => {
        console.log(`FFmpeg progress: ${progress * 100}%`);
      });

      try {
        await ffmpeg.current.load({
          coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
          wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        });
        setFFmpegLoaded(true);
      } catch (error) {
        console.error("Failed to load FFmpeg:", error);
      }
    };

    loadFFmpeg();
  }, []);

  useEffect(() => {
    const storedUserName = localStorage.getItem('user');
    if (storedUserName) {
      setUserName(storedUserName);
      setUserInitial(storedUserName.charAt(0).toUpperCase());
    }
  }, []);

  const formatTime = useCallback((time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }, []);

  const formatCurrentDateTime = useCallback(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = now.toLocaleString('en-US', { month: 'short' });
    const year = now.getFullYear();
    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedTime = `${hours}:${minutes} ${ampm}`;

    return `${day} ${month} ${year} ${formattedTime}`;
  }, []);

  useEffect(() => {
    setCurrentDateTime(formatCurrentDateTime());
    dateTimeIntervalRef.current = setInterval(() => {
      setCurrentDateTime(formatCurrentDateTime());
    }, 1000);

    return () => {
      clearInterval(dateTimeIntervalRef.current);
    };
  }, [formatCurrentDateTime]);

  const stopStream = useCallback(() => {
    try {
      const currentStream = streamRef.current;
      if (currentStream) {
        const tracks = currentStream.getTracks();
        tracks.forEach((track, index) => {
          try {
            track.enabled = false;
            track.stop();
          } catch (trackError) {
            console.error(`Error stopping track ${index}:`, trackError);
          }
        });
        streamRef.current = null;
      }

      if (videoRef.current) {
        try {
          const video = videoRef.current;
          if (video.srcObject) {
            video.pause();
            video.srcObject = null;
            video.src = '';
            video.load();
            video.removeAttribute('src');
          }
        } catch (videoError) {
          console.error('Video cleanup error:', videoError);
        }
      }

      if (audioRef.current) {
        try {
          const audio = audioRef.current;
          if (audio.srcObject) {
            audio.pause();
            audio.srcObject = null;
            audio.src = '';
            audio.load();
            audio.removeAttribute('src');
          }
        } catch (audioError) {
          console.error('Audio cleanup error:', audioError);
        }
      }
    } catch (error) {
      console.error('Error during stream cleanup:', error);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (!isRecording) return;
    setIsRecording(false);

    actualRecordingDuration.current = recordingTime;

    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    clearTimeout(stopTimerRef.current);
    setShowTabSwitchWarning(false);
      stopStream();
    }, [isRecording, recordingTime, stopStream]);

    const startRecording = useCallback(async () => {
      if (!navigator.mediaDevices || !MediaRecorder) {
        console.error('MediaRecorder not supported on this device.');
        alert('Your device does not support recording. Please use a modern browser or device.');
        return;
      }
  
      if (!streamRef.current) {
        console.error("No media stream available to start recording.");
        alert('Failed to access camera or microphone. Please check permissions and try again.');
        setIsCountingDown(false);
        setShowStartMessage(false);
        setHasStartedRecording(false);
        return;
      }
  
      setIsCountingDown(true);
      setCountdown(5);
      setHasStartedRecording(true);
  
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            setIsCountingDown(false);
            setShowStartMessage(true);
            setTimeout(() => {
              setShowStartMessage(false);
  
              if (!streamRef.current) {
                console.error("Media stream lost before starting recording.");
                alert('Failed to start recording. Please try again.');
                setHasStartedRecording(false);
                return;
              }
  
              clearTimeout(stopTimerRef.current);
              setIsRecording(true);
              setVideoBlob(null);
              setVideoURL(null);
              setAudioBlob(null);
              setAudioURL(null);
              setRecordingTime(0);
              actualRecordingDuration.current = 0;
              startTimeRef.current = Date.now();
              setShowTabSwitchWarning(true);
  
              let mediaRecorder;
              const chunks = [];
  
              let mimeType;
              const supportedVideoTypes = [
                'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
                'video/webm;codecs=h264,opus',
                'video/webm;codecs=vp9,opus',
                'video/webm;codecs=vp8,opus',
                'video/mp4',
                'video/webm',
              ];
  
              const supportedAudioTypes = [
                'audio/mp4;codecs=mp4a.40.2',
                'audio/webm;codecs=opus',
                'audio/mp4',
                'audio/webm',
              ];
  
              if (mode === 'audio') {
                mimeType = supportedAudioTypes.find(type => MediaRecorder.isTypeSupported(type)) || 'audio/webm';
                console.log('Selected audio MIME type:', mimeType);
  
                mediaRecorder = new MediaRecorder(streamRef.current, {
                  audioBitsPerSecond: 128000,
                  mimeType,
                });
              } else {
                mimeType = supportedVideoTypes.find(type => MediaRecorder.isTypeSupported(type)) || 'video/webm';
                console.log('Selected video MIME type:', mimeType);
  
                mediaRecorder = new MediaRecorder(streamRef.current, {
                  videoBitsPerSecond: (isSafari || isIOS || isAndroid) ? 5000000 : 8000000,
                  audioBitsPerSecond: 128000,
                  mimeType,
                });
              }
  
              mediaRecorderRef.current = mediaRecorder;
  
              mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                  chunks.push(e.data);
                }
              };
  
              mediaRecorder.onstop = async () => {
                let finalBlob;
                let finalURL;
  
                console.log('Recording stopped, chunks collected:', chunks.length);
                console.log('Actual recording duration:', actualRecordingDuration.current);
  
                const recordedBlob = new Blob(chunks, { type: mediaRecorderRef.current.mimeType });
                console.log('Original blob:', {
                  size: recordedBlob.size,
                  type: recordedBlob.type,
                });
  
                if (ffmpegLoaded) {
                  try {
                    if (mode === 'audio') {
                      finalBlob = await processAudioWithFFmpeg(recordedBlob);
                      setAudioBlob(finalBlob);
                      finalURL = URL.createObjectURL(finalBlob);
                      setAudioURL(finalURL);
                    } else {
                      finalBlob = await processVideoWithFFmpeg(recordedBlob);
                      finalBlob = await fixIOSSafariDuration(finalBlob, actualRecordingDuration.current);
                      setVideoBlob(finalBlob);
                      finalURL = URL.createObjectURL(finalBlob);
                      setVideoURL(finalURL);
                    }
                    console.log('Processed blob size:', finalBlob.size);
                  } catch (error) {
                    console.error('FFmpeg processing failed, falling back to original blob:', error);
                    finalBlob = new Blob(chunks, { type: mediaRecorderRef.current.mimeType });
                    if (mode === 'audio') {
                      setAudioBlob(finalBlob);
                      setAudioURL(URL.createObjectURL(finalBlob));
                    } else {
                      finalBlob = await fixIOSSafariDuration(finalBlob, actualRecordingDuration.current);
                      setVideoBlob(finalBlob);
                      setVideoURL(URL.createObjectURL(finalBlob));
                    }
                  }
                } else {
                  finalBlob = new Blob(chunks, { type: mediaRecorderRef.current.mimeType });
                  if (mode === 'audio') {
                    setAudioBlob(finalBlob);
                    setAudioURL(URL.createObjectURL(finalBlob));
                  } else {
                    finalBlob = await fixIOSSafariDuration(finalBlob, actualRecordingDuration.current);
                    setVideoBlob(finalBlob);
                    setVideoURL(URL.createObjectURL(finalBlob));
                  }
                }
                setShowTabSwitchWarning(false);
              };
  
              const timeslice = (isSafari || isIOS || isAndroid) ? 500 : undefined;
              mediaRecorder.start(timeslice);
  
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
              }
              intervalRef.current = setInterval(() => {
                const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
                setRecordingTime(elapsed);
                actualRecordingDuration.current = elapsed;
  
                if (elapsed >= 120) {
                  mediaRecorder.stop();
                }
              }, 1000);
  
              if (stopTimerRef.current) {
                clearTimeout(stopTimerRef.current);
              }
              stopTimerRef.current = setTimeout(() => {
                if (mediaRecorder.state === 'recording') {
                  mediaRecorder.stop();
                }
              }, 120000);
            }, 1000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, [ffmpegLoaded, mode, isSafari, isIOS, isAndroid, processAudioWithFFmpeg, processVideoWithFFmpeg, fixIOSSafariDuration]);

  useEffect(() => {
    const getCameraAccess = async () => {
      try {
        setCameraLoading(true);
        const constraints = {
          video: mode !== 'audio' ? {
            frameRate: { ideal: (isSafari || isIOS || isAndroid) ? 24 : 30, max: 30, min: 24 },
            width: { ideal: (isSafari || isIOS || isAndroid) ? 854 : 1280, max: 1280 },
            height: { ideal: (isSafari || isIOS || isAndroid) ? 480 : 720, max: 720 },
            facingMode: isIOS ? 'user' : { ideal: 'user' },
          } : false,
          audio: {
            sampleRate: { ideal: 44100, max: 48000 },
            channelCount: 2,
            echoCancellation: true,
            noiseSuppression: true,
          },
        };

        console.log('Requesting media with constraints:', constraints);
        const userMediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        console.log("Audio tracks:", userMediaStream.getAudioTracks()); // Debug audio tracks
        streamRef.current = userMediaStream;

        if (mode !== 'audio' && userMediaStream.getVideoTracks().length > 0) {
          const videoTrack = userMediaStream.getVideoTracks()[0];
          const settings = videoTrack.getSettings();
          console.log('Video track settings:', settings);
        }

        if (mode !== 'audio') {
          if (videoRef.current) {
            videoRef.current.srcObject = userMediaStream;
            if (isSafari || isIOS) {
              videoRef.current.onloadedmetadata = () => {
                videoRef.current.play().catch(e => console.log('Auto-play prevented:', e));
              };
            }
          }
        } else {
          if (audioRef.current) {
            audioRef.current.srcObject = userMediaStream;
          }
        }

        setCameraLoading(false);
        if (streamRef.current) {
          startRecording();
        } else {
          console.error("Failed to initialize media stream.");
          alert('Failed to access camera or microphone. Please check permissions and try again.');
        }
      } catch (error) {
        console.error("Error accessing media:", error);
        alert('Failed to access camera or microphone. Please check permissions and try again.');
        setCameraLoading(false);
      }
    };

    if (ffmpegLoaded) {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
        videoRef.current.removeAttribute("src");
        videoRef.current.load();
      }

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.srcObject = null;
        audioRef.current.removeAttribute("src");
        audioRef.current.load();
      }

      getCameraAccess();
    }

    return () => {
      stopStream();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (stopTimerRef.current) {
        clearTimeout(stopTimerRef.current);
        stopTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    };
  }, [mode, isSafari, isIOS, isAndroid, ffmpegLoaded, startRecording, stopStream]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log("Tab switched or minimized.");
        if (isRecording) {
          console.log("Recording in progress. Stopping recording.");
          stopRecording();
        }
      } else {
        if (isRecording || !hasRecordedMedia) {
          setShowTabSwitchWarning(true);
        } else {
          setShowTabSwitchWarning(false);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isRecording, stopRecording, hasRecordedMedia]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && hasRecordedMedia && !isRecording) {
      video.muted = isMuted;
    }
  }, [isMuted, hasRecordedMedia, isRecording]);

  useEffect(() => {
    if (hasRecordedMedia && !isRecording && mode !== 'audio' && videoRef.current) {
      const video = videoRef.current;
      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      const handleTimeUpdate = () => setCurrentTime(video.currentTime);
      const handleLoadedMetadata = () => setDuration(video.duration);

      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('loadedmetadata', handleLoadedMetadata);

      return () => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [hasRecordedMedia, isRecording, mode]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const seek = (event) => {
    const video = videoRef.current;
    video.currentTime = event.target.value;
  };

  const handleSubmit = () => {
    const blobToSubmit = mode === 'audio' ? audioBlob : videoBlob;
    if (!blobToSubmit) {
      console.error("No media to submit.");
      return;
    }
    if (videoRef.current) videoRef.current.pause();
    if (audioRef.current) audioRef.current.pause();
    setShowComponent(true);
    stopStream();
    setShowTabSwitchWarning(false);
  };

  const handleBack = () => {
    console.log('handleBack called');
    window.location.href = "/upload";
  };

  return (
    <div className="min-h-full flex flex-col items-center p-4 md:p-6">
      <div className="w-full p-2">
        <p className="text-2xl font-semibold font-sans">Assessment</p>
      </div>

      <div className="w-full flex flex-col items-center justify-start lg:max-w-[60%]">
        {mode !== "audio" && (
          <p className="font-bold tracking-wider">Always Look at the Camera</p>
        )}

        {showTabSwitchWarning && (isRecording || !hasRecordedMedia) && (
          <p className="text-red-600 text-sm mt-2 text-center">
            **Don't switch tabs.** If you switch the tab, recording will be stopped automatically.
          </p>
        )}

        {!isRecording && !hasRecordedMedia && !isCountingDown && !showStartMessage && !hasStartedRecording && (
          <button
            className="relative left-[-47%] flex items-center cursor-pointer"
            onClick={handleBack}
          >
            <IoMdArrowRoundBack className="me-2" />
            Back
          </button>
        )}

        <div className={`relative flex flex-col w-full rounded-xl
          ${mode === 'audio' ? 'bg-gradient-to-b from-[#073088] to-[#020C22]' : 'bg-gray-200'}
            mt-2 overflow-hidden aspect-[9/8] md:aspect-[16/9]`}>
          {cameraLoading && mode !== 'audio' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-400 bg-opacity-75 text-white z-10">
              <p className="text-sm md:text-base lg:text-lg">Loading Camera...</p>
            </div>
          )}

          <div className="media-player-content w-full h-full relative">
            {(!hasRecordedMedia || isRecording) && (
              <>
                {mode !== 'audio' ? (
                  <video
                    ref={videoRef}
                    className="rounded-lg object-cover w-full h-full"
                    autoPlay
                    muted
                    srcObject={streamRef.current}
                    playsInline
                    webkit-playsinline="true"
                    style={{ transform: 'scaleX(-1)' }}
                  />
                ) : (
                  <audio
                    ref={audioRef}
                    className="rounded-lg object-cover w-full h-full"
                    autoPlay
                    muted={!hasRecordedMedia}
                    srcObject={streamRef.current}
                    controls={false}
                    playsInline
                    webkit-playsinline="true"
                  />
                )}
              </>
            )}
            {hasRecordedMedia && !isRecording && (
              <>
                {mode !== 'audio' ? (
                  <div className="w-full h-full relative">
                    <video
                      ref={videoRef}
                      className="rounded-lg object-cover w-full h-full"
                      style={{ transform: 'scaleX(-1)' }}
                      autoPlay
                      muted={isMuted}
                      src={videoURL || undefined}
                      playsInline
                      controls={false}
                    />

                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 px-3 py-2 flex flex-col gap-1 z-20">
                      <div
                        className="relative w-full h-1.5 bg-white bg-opacity-30 rounded-full cursor-pointer"
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const percent = (e.clientX - rect.left) / rect.width;
                          seek({ target: { value: percent * duration } });
                        }}
                      >
                        <div
                          className="absolute top-0 left-0 h-full bg-white bg-opacity-40"
                          style={{ width: `${(currentTime / duration) * 100}%`, transition: "width 0.2s" }}
                        />
                      </div>

                      <div className="flex items-center justify-between w-full pt-2 text-white text-sm">
                        <div className="flex items-center gap-4">
                          <button onClick={togglePlayPause} className="hover:text-gray-300 transition">
                            {isPlaying ? <FaPause /> : <FaPlay />}
                          </button>

                          <span className="font-mono text-xs sm:text-sm">
                            {formatTime(Math.floor(currentTime))} / {formatTime(Math.floor(duration))}
                          </span>
                        </div>

                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setIsMuted(!isMuted)}
                            className="hover:text-gray-300 transition"
                          >
                            {isMuted ? (
                              <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16" className="w-4 h-4">
                                <path d="M9.717 3.55L5.432 7H2v2h3.432l4.285 3.45A1 1 0 0011 11.708V4.292a1 1 0 00-1.283-.742z" />
                                <path fillRule="evenodd" d="M12.146 4.854a.5.5 0 010 .708L10.207 7.5l1.939 1.938a.5.5 0 01-.708.708L9.5 8.207l-1.938 1.939a.5.5 0 01-.708-.708L8.793 7.5 6.854 5.561a.5.5 0 11.708-.708L9.5 6.793l1.938-1.939a.5.5 0 01.708 0z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16" className="w-4 h-4">
                                <path d="M11.536 14.01A8.951 8.951 0 0016 8a8.951 8.951 0 00-4.464-6.01l-.707.707A7.951 7.951 0 0115 8a7.951 7.951 0 01-3.171 6.303l.707.707z" />
                                <path d="M9.536 12.01A6.951 6.951 0 0014 8a6.951 6.951 0 00-4.464-6.01l-.707.707A5.951 5.951 0 0113 8a5.951 5.951 0 01-2.171 4.303l.707.707z" />
                                <path d="M7 3.293l-3 3H1v3h3l3 3V3.293z" />
                              </svg>
                            )}
                          </button>

                          <button
                            onClick={toggleFullScreen}
                            className="hover:text-gray-300 transition"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                              className="w-4 h-4"
                            >
                              <path d="M1 1h5v2H3v3H1V1zm14 0v5h-2V3h-3V1h5zm0 14h-5v-2h3v-3h2v5zM1 15v-5h2v3h3v2H1z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <audio
                    ref={audioRef}
                    className="rounded-lg object-cover w-full h-full pb-5"
                    autoPlay
                    muted={false}
                    controls
                    src={audioURL || undefined}
                    controlsList="nodownload noremoteplayback"
                    webkit-playsinline="true"
                    playsInline
                  />
                )}
              </>
            )}

            {(isCountingDown || showStartMessage) && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75 text-white z-20">
                {isCountingDown ? (
                  <p className="text-6xl font-bold">{countdown}</p>
                ) : (
                  <p className="text-2xl font-bold">Let's start recording</p>
                )}
              </div>
            )}
          </div>

          {mode === 'audio' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="w-40 h-40 rounded-full flex items-center justify-center border-2 border-solid" style={{ borderColor: '#E72C79' }}>
                <div className="relative w-32 h-32 rounded-full bg-blue-500 flex items-center justify-center text-white text-5xl font-bold">
                  {userInitial}
                  {isRecording && (
                    <div className="absolute bottom-2 w-full flex justify-center">
                      <div className="w-24 h-8 overflow-hidden">
                        <WaveAmplifier stream={streamRef.current} amplitude={3} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-4 text-white text-lg font-semibold" style={{ fontFamily: "sans-serif, Archivo" }}>
                {userName}
              </p>
            </div>
          )}

          <div className="absolute lg:top-10 lg:right-10 top-5 right-5 w-[40%] sm:w-[25%] md:w-[15%] h-[10%] sm:h-[8%] md:h-[7%] flex justify-center rounded-full bg-[#00000033]">
            {mode === 'fm' ? (
              <div className="w-full h-full flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
               <path d="M7.99956 8.00107C9.40537 8.00107 10.545 6.86143 10.545 5.45561C10.545 4.0498 9.40537 2.91016 7.99956 2.91016C6.59374 2.91016 5.4541 4.0498 5.4541 5.45561C5.4541 6.86143 6.59374 8.00107 7.99956 8.00107Z" fill="white"/>
               <path d="M13.0909 11.7091C13.0911 11.3077 12.9805 10.914 12.7713 10.5714C12.5622 10.2288 12.2626 9.95058 11.9055 9.76727C10.7029 9.1198 9.36526 8.76358 8 8.72727C6.63474 8.76358 5.29706 9.1198 4.09455 9.76727C3.73745 9.95058 3.43784 10.2288 3.22869 10.5714C3.01953 10.914 2.90895 11.3077 2.90909 11.7091V12.0436C2.90813 12.1808 2.93432 12.3168 2.98616 12.4439C3.03799 12.5709 3.11445 12.6864 3.21112 12.7837C3.30779 12.8811 3.42276 12.9583 3.54941 13.0111C3.67607 13.0638 3.8119 13.0909 3.94909 13.0909H12.0509C12.1881 13.0909 12.3239 13.0638 12.4506 13.0111C12.5772 12.9583 12.6922 12.8811 12.7889 12.7837C12.8856 12.6864 12.962 12.5709 13.0138 12.4439C13.0657 12.3168 13.0919 12.1808 13.0909 12.0436V11.7091ZM0.363636 2.90909H1.09091C1.18735 2.90909 1.27984 2.87078 1.34804 2.80258C1.41623 2.73439 1.45455 2.6419 1.45455 2.54545V1.45455H2.54545C2.6419 1.45455 2.73439 1.41623 2.80258 1.34804C2.87078 1.27984 2.90909 1.18735 2.90909 1.09091V0.363636C2.90909 0.267194 2.87078 0.174702 2.80258 0.106507C2.73439 0.0383116 2.6419 0 2.54545 0H1.45455C1.06878 0 0.698807 0.153246 0.426027 0.426027C0.153246 0.698807 0 1.06878 0 1.45455V2.54545C0 2.6419 0.0383116 2.73439 0.106507 2.80258C0.174702 2.87078 0.267194 2.90909 0.363636 2.90909ZM14.5455 0H13.4545C13.3581 0 13.2656 0.0383116 13.1974 0.106507C13.1292 0.174702 13.0909 0.267194 13.0909 0.363636V1.09091C13.0909 1.18735 13.1292 1.27984 13.1974 1.34804C13.2656 1.41623 13.3581 1.45455 13.4545 1.45455H14.5455V2.54545C14.5455 2.6419 14.5838 2.73439 14.652 2.80258C14.7202 2.87078 14.8126 2.90909 14.9091 2.90909H15.6364C15.7328 2.90909 15.8253 2.87078 15.8935 2.80258C15.9617 2.73439 16 2.6419 16 2.54545V1.45455C16 1.06878 15.8468 0.698807 15.574 0.426027C15.3012 0.153246 14.9312 0 14.5455 0ZM15.6364 13.0909H14.9091C14.8126 13.0909 14.7202 13.1292 14.652 13.1974C14.5838 13.2656 14.5455 13.3581 14.5455 13.4545V14.5455H13.4545C13.3581 14.5455 13.2656 14.5838 13.1974 14.652C13.1292 14.7202 13.0909 14.8126 13.0909 14.9091V15.6364C13.0909 15.7328 13.1292 15.8253 13.1974 15.8935C13.2656 15.9617 13.3581 16 13.4545 16H14.5455C14.9312 16 15.3012 15.8468 15.574 15.574C15.8468 15.3012 16 14.9312 16 14.5455V13.4545C16 13.3581 15.9617 13.2656 15.8935 13.1974C15.8253 13.1292 15.7328 13.0909 15.6364 13.0909ZM2.54545 14.5455H1.45455V13.4545C1.45455 13.3581 1.41623 13.2656 1.34804 13.1974C1.27984 13.1292 1.18735 13.0909 1.09091 13.0909H0.363636C0.267194 13.0909 0.174702 13.1292 0.106507 13.1974C0.0383116 13.2656 0 13.3581 0 13.4545V14.5455C0 14.9312 0.153246 15.3012 0.426027 15.574C0.698807 15.8468 1.06878 16 1.45455 16H2.54545C2.6419 16 2.73439 15.9617 2.80258 15.8935C2.87078 15.8253 2.90909 15.7328 2.90909 15.6364V14.9091C2.90909 14.8126 2.87078 14.7202 2.80258 14.652C2.73439 14.5838 2.6419 14.5455 2.54545 14.5455Z" fill="white"/>
            </svg>
                <p className="text-sm font-medium ms-5 text-[#ffffff]" style={{ fontFamily: "sans-serif, Archivo" }}>Face Mode</p>
              </div>
            ) : mode === 'bm' ? (
              <div className="w-full h-full flex items-center justify-center">
              <svg width="12" height="16" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                   <path d="M5.99007 3.32779C6.90901 3.32779 7.65396 2.58284 7.65396 1.66389C7.65396 0.74495 6.90901 0 5.99007 0C5.07112 0 4.32617 0.74495 4.32617 1.66389C4.32617 2.58284 5.07112 3.32779 5.99007 3.32779Z" fill="white"/>
                   <path d="M9.3178 5.68386C9.31979 5.51955 9.28063 5.35736 9.20391 5.21206C9.12719 5.06675 9.01534 4.94294 8.87854 4.85191C8.01538 4.30227 7.01332 4.0103 5.99002 4.0103C4.96671 4.0103 3.96465 4.30227 3.1015 4.85191C2.96427 4.9425 2.85208 5.06621 2.7753 5.21162C2.69853 5.35703 2.65964 5.51945 2.66223 5.68386V10.3428C2.66223 10.431 2.69729 10.5157 2.7597 10.5781C2.82211 10.6405 2.90675 10.6755 2.99501 10.6755H3.99334V15.6672C3.99334 15.7555 4.02841 15.8401 4.09081 15.9025C4.15322 15.9649 4.23786 16 4.32612 16H7.65391C7.74217 16 7.82681 15.9649 7.88922 15.9025C7.95163 15.8401 7.98669 15.7555 7.98669 15.6672V10.6489H8.98503C9.07328 10.6489 9.15793 10.6139 9.22034 10.5514C9.28274 10.489 9.3178 10.4044 9.3178 10.3161V5.68386ZM0.332779 2.66223H0.998336C1.08659 2.66223 1.17124 2.62717 1.23365 2.56476C1.29605 2.50235 1.33111 2.41771 1.33111 2.32945V1.33111H2.32945C2.41771 1.33111 2.50235 1.29605 2.56476 1.23365C2.62717 1.17124 2.66223 1.08659 2.66223 0.998336V0.332779C2.66223 0.24452 2.62717 0.159877 2.56476 0.0974686C2.50235 0.0350605 2.41771 0 2.32945 0L1.33111 0C0.978081 0 0.639507 0.140242 0.389874 0.389875C0.140242 0.639507 0 0.978081 0 1.33111V2.32945C0 2.41771 0.0350606 2.50235 0.0974687 2.56476C0.159877 2.62717 0.24452 2.66223 0.332779 2.66223ZM10.6489 0H9.65058C9.56232 0 9.47768 0.0350605 9.41527 0.0974686C9.35286 0.159877 9.3178 0.24452 9.3178 0.332779V0.998336C9.3178 1.08659 9.35286 1.17124 9.41527 1.23365C9.47768 1.29605 9.56232 1.33111 9.65058 1.33111H10.6489V2.32945C10.6489 2.41771 10.684 2.50235 10.7464 2.56476C10.8088 2.62717 10.8934 2.66223 10.9817 2.66223H11.6473C11.7355 2.66223 11.8202 2.62717 11.8826 2.56476C11.945 2.50235 11.98 2.41771 11.98 2.32945V1.33111C11.98 0.978081 11.8398 0.639507 11.5902 0.389875C11.3405 0.140242 11.002 0 10.6489 0ZM11.6473 13.3111H10.9817C10.8934 13.3111 10.8088 13.3462 10.7464 13.4086C10.684 13.471 10.6489 13.5557 10.6489 13.6439V14.6423H9.65058C9.56232 14.6423 9.47768 14.6773 9.41527 14.7397C9.35286 14.8021 9.3178 14.8868 9.3178 14.975V15.6406C9.3178 15.7289 9.35286 15.8135 9.41527 15.8759C9.47768 15.9383 9.56232 15.9734 9.65058 15.9734H10.6489C11.002 15.9734 11.3405 15.8331 11.5902 15.5835C11.8398 15.3339 11.98 14.9953 11.98 14.6423V13.6439C11.98 13.5557 11.945 13.471 11.8826 13.4086C11.8202 13.3462 11.7355 13.3111 11.6473 13.3111ZM2.32945 14.6423H1.33111V13.6439C1.33111 13.5557 1.29605 13.471 1.23365 13.4086C1.17124 13.3462 1.08659 13.3111 0.998336 13.3111H0.332779C0.24452 13.3111 0.159877 13.3462 0.0974687 13.4086C0.0350606 13.471 0 13.5557 0 13.6439V14.6423C0 14.9953 0.140242 15.3339 0.389874 15.5835C0.639507 15.8331 0.978081 15.9734 1.33111 15.9734H2.32945C2.41771 15.9734 2.50235 15.9383 2.56476 15.8759C2.62717 15.8135 2.66223 15.7289 2.66223 15.6406V14.975C2.66223 14.8868 2.62717 14.8021 2.56476 14.7397C2.50235 14.6773 2.41771 14.6423 2.32945 14.6423Z" fill="white"/>
              </svg>
                <p className="text-sm font-medium ms-5 text-[#ffffff]" style={{ fontFamily: "sans-serif, Archivo" }}>Body Mode</p>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
               <svg width="21" height="16" viewBox="0 0 21 16" fill="none" xmlns="http://www.w3.org/2000/svg">
               <path d="M18.15 12.8743C18.0704 12.8743 17.9941 12.8452 17.9379 12.7935C17.8816 12.7418 17.85 12.6716 17.85 12.5985V3.40178C17.85 3.32863 17.8816 3.25848 17.9379 3.20676C17.9941 3.15503 18.0704 3.12597 18.15 3.12597C18.2296 3.12597 18.3059 3.15503 18.3621 3.20676C18.4184 3.25848 18.45 3.32863 18.45 3.40178V12.5985C18.45 12.6716 18.4184 12.7418 18.3621 12.7935C18.3059 12.8452 18.2296 12.8743 18.15 12.8743ZM10.5 12.8743C10.4204 12.8743 10.3441 12.8452 10.2879 12.7935C10.2316 12.7418 10.2 12.6716 10.2 12.5985V3.40178C10.2 3.32863 10.2316 3.25848 10.2879 3.20676C10.3441 3.15503 10.4204 3.12597 10.5 3.12597C10.5796 3.12597 10.6559 3.15503 10.7121 3.20676C10.7684 3.25848 10.8 3.32863 10.8 3.40178V12.5985C10.8 12.6716 10.7684 12.7418 10.7121 12.7935C10.6559 12.8452 10.5796 12.8743 10.5 12.8743ZM15.6 14.0465C15.5204 14.0465 15.4441 14.0174 15.3879 13.9657C15.3316 13.914 15.3 13.8438 15.3 13.7707V2.22961C15.3 2.15646 15.3316 2.08631 15.3879 2.03458C15.4441 1.98286 15.5204 1.9538 15.6 1.9538C15.6796 1.9538 15.7559 1.98286 15.8121 2.03458C15.8684 2.08631 15.9 2.15646 15.9 2.22961V13.7707C15.9 13.8438 15.8684 13.914 15.8121 13.9657C15.7559 14.0174 15.6796 14.0465 15.6 14.0465ZM13.05 16C12.9704 16 12.8941 15.9709 12.8379 15.9192C12.7816 15.8675 12.75 15.7973 12.75 15.7242V0.275805C12.75 0.202657 12.7816 0.132505 12.8379 0.0807813C12.8941 0.0290579 12.9704 0 13.05 0C13.1296 0 13.2059 0.0290579 13.2621 0.0807813C13.3184 0.132505 13.35 0.202657 13.35 0.275805V15.7242C13.35 15.7973 13.3184 15.8675 13.2621 15.9192C13.2059 15.9709 13.1296 16 13.05 16ZM2.85 12.8743C2.77044 12.8743 2.69413 12.8452 2.63787 12.7935C2.58161 12.7418 2.55 12.6716 2.55 12.5985V3.40178C2.55 3.32863 2.58161 3.25848 2.63787 3.20676C2.69413 3.15503 2.77044 3.12597 2.85 3.12597C2.92956 3.12597 3.00587 3.15503 3.06213 3.20676C3.11839 3.25848 3.15 3.32863 3.15 3.40178V12.5985C3.15 12.6716 3.11839 12.7418 3.06213 12.7935C3.00587 12.8452 2.92956 12.8743 2.85 12.8743ZM5.4 11.3113C5.32044 11.3113 5.24413 11.2823 5.18787 11.2305C5.13161 11.1788 5.1 11.1087 5.1 11.0355V4.96449C5.1 4.89134 5.13161 4.82119 5.18787 4.76947C5.24413 4.71774 5.32044 4.68869 5.4 4.68869C5.47957 4.68869 5.55587 4.71774 5.61213 4.76947C5.66839 4.82119 5.7 4.89134 5.7 4.96449V11.0355C5.7 11.1087 5.66839 11.1788 5.61213 11.2305C5.55587 11.2823 5.47957 11.3113 5.4 11.3113ZM7.95 16C7.87044 16 7.79413 15.9709 7.73787 15.9192C7.68161 15.8675 7.65 15.7973 7.65 15.7242V0.275805C7.65 0.202657 7.68161 0.132505 7.73787 0.0807813C7.79413 0.0290579 7.87044 0 7.95 0C8.02956 0 8.10587 0.0290579 8.16213 0.0807813C8.21839 0.132505 8.25 0.202657 8.25 0.275805V15.7242C8.25 15.7973 8.21839 15.8675 8.16213 15.9192C8.10587 15.9709 8.02956 16 7.95 16ZM20.7 11.4418C20.6204 11.4418 20.5441 11.4127 20.4879 11.361C20.4316 11.3093 20.4 11.2391 20.4 11.166V4.83431C20.4 4.76116 20.4316 4.69101 20.4879 4.63929C20.5441 4.58756 20.6204 4.55851 20.7 4.55851C20.7796 4.55851 20.8559 4.58756 20.9121 4.63929C20.9684 4.69101 21 4.76116 21 4.83431V11.166C21 11.2391 20.9684 11.3093 20.9121 11.361C20.8559 11.4127 20.7796 11.4418 20.7 11.4418ZM0.3 9.73426C0.220435 9.73426 0.144129 9.7052 0.087868 9.65348C0.031607 9.60176 0 9.53161 0 9.45846V6.54182C0 6.46867 0.031607 6.39852 0.087868 6.3468C0.144129 6.29507 0.220435 6.26601 0.3 6.26601C0.379565 6.26601 0.455871 6.29507 0.512132 6.3468C0.568393 6.39852 0.6 6.46867 0.6 6.54182V9.45846C0.6 9.53161 0.568393 9.60176 0.512132 9.65348C0.455871 9.7052 0.379565 9.73426 0.3 9.73426Z" fill="white"/>
               </svg>
                <p className="text-sm font-medium ms-5 text-[#ffffff]" style={{ fontFamily: "sans-serif, Archivo" }}>Audio Mode</p>
              </div>
            )}
          </div>

          <div className="absolute bottom-8 w-full flex justify-center">
            {isRecording && (
              <div
                className="bg-gray-300 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full flex items-center min-w-[180px] justify-between"
                onClick={stopRecording}
              >
                <span className="text-[#FF6F6F] font-semibold mx-2 sm:mx-4 text-sm sm:text-base flex items-center gap-2">
                  <WaveAmplifier stream={streamRef.current} />
                  <div className="w-[40px] text-center font-mono tracking-widest tabular-nums inline-block">
                    {formatTime(recordingTime)}
                  </div>
                  <span className="ml-3">Stop</span>
                </span>
                <button
                  onClick={stopRecording}
                  className="ml-2 sm:ml-1 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-red-100 rounded-full"
                >
                  <FontAwesomeIcon
                    icon={faStop}
                    color="red"
                    className="w-4 sm:w-4 h-4 sm:h-4"
                  />
                </button>
              </div>
            )}
          </div>

          {mode === 'audio' && isRecording && (
            <div className="absolute bottom-4 left-4 text-white text-xs sm:text-sm font-medium" style={{ fontFamily: "sans-serif, Archivo", whiteSpace: "nowrap" }}>
              {currentDateTime}
            </div>
          )}
        </div>

        <div className="w-full flex flex-col md:flex-row items-center justify-center p-4">
          {isRecording ? null : hasRecordedMedia && (
            <div className="flex flex-col md:flex-row gap-4 w-full max-w-md">
              <button
                className="w-full h-[80%] flex items-center justify-center bg-[#2563EB] text-white py-3 px-6 rounded-full hover:bg-blue-600 transition duration-300 text-lg"
                onClick={handleSubmit}
                style={{ fontFamily: "sans-serif, Archivo" }}
              >
                Submit
              </button>
              <button
                className="w-full h-[80%] flex items-center justify-center border border-[#2563EB] text-[#2563EB] py-3 px-6 rounded-full hover:bg-[#E0F2FE] transition duration-300 text-lg"
                onClick={async () => {
                  try {
                    stopStream();
                    setVideoBlob(null);
                    setVideoURL(null);
                    setAudioBlob(null);
                    setAudioURL(null);
                    setRecordingTime(0);
                    setShowTabSwitchWarning(true);
                    setHasStartedRecording(false);
                    if (isSafari || isIOS) {
                      window.location.href = "/upload?reset=" + Date.now();
                    } else {
                      window.location.reload();
                    }
                  } catch (e) {
                    console.error("Error on retake reload:", e);
                  }
                }}
                style={{ fontFamily: "sans-serif, Archivo" }}
              >
                Retake
              </button>
            </div>
          )}
        </div>

        {showComponent && (
          <UploadPopup
            onClose={() => setShowComponent(false)}
            popupstatus="submit"
            Mode={mode}
            videodata={mode === 'audio' ? audioBlob : videoBlob}
            videotype={"self_recorded"}
          />
        )}
      </div>
    </div>
  );
};

export default VideoRecording;